import { $agriculture } from '@/modules/agriculture/store/agriculture.store';
import {
  AGRICULTURES,
  SEARCH_BY_RANGES_LOCAL_STORAGE_KEY,
} from '@/modules/search/constants';
import { getWheatClass } from '@/modules/search/functions/get-wheat-class.function';
import { useIndicateClass } from '@/modules/search/hooks/indicate-class.hook';
import {
  $searchByRanges,
  setSpecificRangeValue,
  setValuesToSearchByRanges,
  setWarehousesToSearchByRanges,
} from '@/modules/search/store/search-by-params.store';
import { IWheatData } from '@/modules/search/ts/index.types';
import { Col, Row } from 'antd';
import { useStore } from 'effector-react';
import { debounce, isEqual } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { MidFiltersBar } from '../../../by-params-search/components/mid-filters-bar';
import { RangeSlider } from '../../../range-slider';
import styles from './index.module.sass';
import { EXTREME_INDICATORS } from '@/modules/mixing/constants/extreme-indicators.constants';

export const MidRangesBar = ({
  setRangesStateToDefault,
}: {
  setRangesStateToDefault: () => void;
}) => {
  const { reIndicateClassAction, reIndicateClassState } = useIndicateClass();
  const agricultures = useStore($agriculture);
  const wheat = agricultures.find((item) => item.name === AGRICULTURES[0].name);
  const wheatRanges = {
    gluten: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[0].name,
    ),
    humidity: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[1].name,
    ),
    idk: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[2].name,
    ),
    cp: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[3].name,
    ),
    protein: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[4].name,
    ),
    nature: wheat?.parameters.find(
      (item) => item.name === AGRICULTURES[0].parameters[5].name,
    ),
  };

  const searchState = useStore($searchByRanges);

  const getClassByRanges = useCallback(
    debounce(async () => {
      const result_class = await getWheatClass(searchState);
      const cachedSearchState = localStorage.getItem(
        SEARCH_BY_RANGES_LOCAL_STORAGE_KEY,
      );
      const parsed = cachedSearchState ? JSON.parse(cachedSearchState) : null;
      setWarehousesToSearchByRanges({
        ...searchState,
        stores: parsed?.stores,
        wheatData: { ...searchState.wheatData, class: +result_class },
      });
    }, 100),
    [searchState],
  );
  useEffect(() => {
    getClassByRanges();
  }, [reIndicateClassState]);
  return (
    <MidFiltersBar
      state={searchState.wheatData}
      setState={(val: IWheatData) =>
        setValuesToSearchByRanges({ ...searchState, wheatData: val })
      }
      reIndicateClassAction={reIndicateClassAction}
      classesBarHidden={true}
      setRangesStateToDefault={setRangesStateToDefault}
    >
      {
        <div className={styles.root}>
          <Row gutter={16} className={styles.span}>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(new Date().getFullYear() - 5)}
                max={String(new Date().getFullYear())}
                value={[
                  String(searchState.rangesData.year[0]),
                  String(searchState.rangesData.year[1]),
                ]}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'year', val: val })
                }
                fieldLabel="год урожая"
                postfix=""
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.gluten.min) || '0'}
                max={String(EXTREME_INDICATORS.gluten.max) || '1000'}
                value={searchState.rangesData.gluten as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'gluten', val: val })
                }
                fieldLabel="клейковина"
                postfix="%"
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.idk.min)}
                max={String(EXTREME_INDICATORS.idk.max)}
                value={searchState.rangesData.idk as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'idk', val: val })
                }
                fieldLabel="ИДК"
                postfix=""
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.bug.min)}
                max={String(EXTREME_INDICATORS.bug.max)}
                value={searchState.rangesData.clop as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'clop', val: val })
                }
                fieldLabel="клоп"
                postfix="%"
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.protein.min)}
                max={String(EXTREME_INDICATORS.protein.max)}
                value={searchState.rangesData.protein as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'protein', val: val })
                }
                fieldLabel="протеин"
                postfix="%"
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.cp.min)}
                max={String(EXTREME_INDICATORS.cp.max)}
                value={searchState.rangesData.cp as any}
                setValue={(val) => setSpecificRangeValue({ fieldName: 'cp', val: val })}
                fieldLabel="число падения"
                postfix="сек"
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.nature.min)}
                max={String(EXTREME_INDICATORS.nature.max)}
                value={searchState.rangesData.nature as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'nature', val: val })
                }
                fieldLabel="натура"
                postfix="г/л"
              />
            </Col>
            <Col span={6} className={styles.col}>
              <RangeSlider
                min={String(EXTREME_INDICATORS.humidity.min)}
                max={String(EXTREME_INDICATORS.humidity.max)}
                value={searchState.rangesData.humidity as any}
                setValue={(val) =>
                  setSpecificRangeValue({ fieldName: 'humidity', val: val })
                }
                fieldLabel="влажность"
                postfix="%"
              />
            </Col>
          </Row>
        </div>
      }
    </MidFiltersBar>
  );
};
